export type SectionHtmlValuesByLanguage = Record<string, Record<string, string>>;
export type SectionElementsByLanguage = Record<string, string>;
export type SectionContentByLanguage = Record<string, string>;
export type SectionHtmlFieldsByLanguage = Record<string, string[]>;

type CanonicalBuildResult = {
  ok: boolean;
  formatted: string;
  minified: string;
};

type ApplyHtmlEditorFieldDraftsArgs = {
  field: string;
  htmlEditorDraftByLanguage: Record<string, string>;
  draftHtmlByLanguage: SectionHtmlValuesByLanguage;
  draftHtmlFieldsByLanguage: SectionHtmlFieldsByLanguage;
  draftElementsByLanguage: SectionElementsByLanguage;
  draftContentByLanguage: SectionContentByLanguage;
  languageKeys: string[];
  getElementsTextForLanguage: (languageKey: string) => string;
  getVisibleHtmlFieldsForLanguage: (languageKey: string) => string[];
  buildCanonicalRawContent: (elementsText: string, htmlValues: Record<string, string>) => CanonicalBuildResult;
  orderHtmlFieldKeys: (fields: string[]) => string[];
};

export function applyHtmlEditorFieldDrafts({
  field,
  htmlEditorDraftByLanguage,
  draftHtmlByLanguage,
  draftHtmlFieldsByLanguage,
  draftElementsByLanguage,
  draftContentByLanguage,
  languageKeys,
  getElementsTextForLanguage,
  getVisibleHtmlFieldsForLanguage,
  buildCanonicalRawContent,
  orderHtmlFieldKeys,
}: ApplyHtmlEditorFieldDraftsArgs) {
  const nextDraftHtmlByLanguage: SectionHtmlValuesByLanguage = { ...draftHtmlByLanguage };
  const nextDraftHtmlFieldsByLanguage: SectionHtmlFieldsByLanguage = { ...draftHtmlFieldsByLanguage };

  for (const langKey of languageKeys) {
    const existingValues = { ...(draftHtmlByLanguage?.[langKey] || {}) };
    const nextValue = typeof htmlEditorDraftByLanguage?.[langKey] === "string"
      ? htmlEditorDraftByLanguage[langKey]
      : (existingValues[field] || "");

    nextDraftHtmlByLanguage[langKey] = {
      ...existingValues,
      [field]: nextValue,
    };

    const fieldSet = new Set(getVisibleHtmlFieldsForLanguage(langKey));
    if (nextValue.trim().length > 0) fieldSet.add(field);
    else fieldSet.delete(field);
    nextDraftHtmlFieldsByLanguage[langKey] = orderHtmlFieldKeys(Array.from(fieldSet));
  }

  const nextDraftContentByLanguage: SectionContentByLanguage = { ...draftContentByLanguage };
  for (const langKey of languageKeys) {
    const elementsText = getElementsTextForLanguage(langKey);
    const canonical = buildCanonicalRawContent(elementsText, nextDraftHtmlByLanguage[langKey] || {});
    if (canonical.ok) {
      nextDraftContentByLanguage[langKey] = canonical.formatted;
    }
  }

  return {
    draftHtmlByLanguage: nextDraftHtmlByLanguage,
    draftHtmlFieldsByLanguage: nextDraftHtmlFieldsByLanguage,
    draftContentByLanguage: nextDraftContentByLanguage,
    draftElementsByLanguage,
  };
}
